import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, Eye, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CustomersPage = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteCustomerId, setDeleteCustomerId] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    public_adjusting_firm: 'Apicella',
    date_of_assignment: '',
    source: '',
    name: '',
    risk_address: '',
    phone: '',
    email: '',
    insurance_company: '',
    date_of_loss: '',
    loss_type: '',
    notes: '',
    assigned_to: 'office_web'
  });
  const [editFormData, setEditFormData] = useState({
    public_adjusting_firm: 'Apicella',
    date_of_assignment: '',
    source: '',
    name: '',
    risk_address: '',
    phone: '',
    email: '',
    insurance_company: '',
    date_of_loss: '',
    loss_type: '',
    notes: '',
    assigned_to: 'office_web'
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = search ? `?search=${search}` : '';
      
      const [customersRes, usersRes] = await Promise.all([
        axios.get(`${API}/customers${params}`),
        axios.get(`${API}/users/list`)
      ]);
      
      // Create user lookup map
      const userMap = { 'office_web': 'Office/Web' };
      usersRes.data.forEach(user => {
        userMap[user.user_id] = user.name;
      });
      
      // Enrich customers with assigned user name
      const enrichedCustomers = customersRes.data.map(customer => ({
        ...customer,
        assigned_to_name: userMap[customer.assigned_to] || 'Office/Web'
      }));
      
      setCustomers(enrichedCustomers);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/customers`, formData);
      
      toast.success('Customer created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  const resetForm = () => {
    setFormData({
      public_adjusting_firm: 'Apicella',
      date_of_assignment: '',
      source: '',
      name: '',
      risk_address: '',
      phone: '',
      email: '',
      insurance_company: '',
      date_of_loss: '',
      loss_type: '',
      notes: '',
      assigned_to: 'office_web'
    });
  };

  const handleEditClick = (customer) => {
    setSelectedCustomer(customer);
    setEditFormData({
      public_adjusting_firm: customer.public_adjusting_firm,
      date_of_assignment: customer.date_of_assignment,
      source: customer.source,
      name: customer.name,
      risk_address: customer.risk_address,
      phone: customer.phone,
      email: customer.email,
      insurance_company: customer.insurance_company,
      date_of_loss: customer.date_of_loss,
      loss_type: customer.loss_type,
      notes: customer.notes || '',
      assigned_to: customer.assigned_to || 'office_web'
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/customers/${selectedCustomer.customer_id}`, editFormData);
      
      toast.success('Customer updated successfully');
      setIsEditDialogOpen(false);
      setSelectedCustomer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    }
  };

  const handleDeleteClick = (customerId) => {
    setDeleteCustomerId(customerId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteCustomer = async () => {
    if (!deleteCustomerId) return;
    
    try {
      await axios.delete(`${API}/customers/${deleteCustomerId}`);
      
      toast.success('Customer deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeleteCustomerId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const canAddCustomer = hasRole(['admin', 'manager', 'staff']);
  const canDeleteCustomer = hasRole(['admin', 'manager']);

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="customers-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold font-sans text-foreground tracking-tight">Customers</h1>
            <p className="text-foreground-muted mt-2">Manage customer information and contacts</p>
          </div>
          {canAddCustomer && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent hover:bg-accent-hover text-accent-foreground" data-testid="add-customer-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Customer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-sans">Add New Customer</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="customer-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="public_adjusting_firm">Public Adjusting Firm <span className="text-red-500">*</span></Label>
                      <Select 
                        value={formData.public_adjusting_firm} 
                        onValueChange={(value) => setFormData({...formData, public_adjusting_firm: value})}
                      >
                        <SelectTrigger data-testid="firm-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Apicella">Apicella</SelectItem>
                          <SelectItem value="MHA">MHA</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="date_of_assignment">Date of Assignment <span className="text-red-500">*</span></Label>
                      <Input
                        id="date_of_assignment"
                        type="date"
                        value={formData.date_of_assignment}
                        onChange={(e) => setFormData({...formData, date_of_assignment: e.target.value})}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="source">Source <span className="text-red-500">*</span></Label>
                      <Input
                        id="source"
                        value={formData.source}
                        onChange={(e) => setFormData({...formData, source: e.target.value})}
                        placeholder="e.g., Referral, Direct"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="name">Customer Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Full name"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="risk_address">Risk Address <span className="text-red-500">*</span></Label>
                      <Input
                        id="risk_address"
                        value={formData.risk_address}
                        onChange={(e) => setFormData({...formData, risk_address: e.target.value})}
                        placeholder="Property address"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="(555) 555-5555"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="customer@example.com"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="insurance_company">Insurance Company <span className="text-red-500">*</span></Label>
                      <Input
                        id="insurance_company"
                        value={formData.insurance_company}
                        onChange={(e) => setFormData({...formData, insurance_company: e.target.value})}
                        placeholder="Company name"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="date_of_loss">Date of Loss <span className="text-red-500">*</span></Label>
                      <Input
                        id="date_of_loss"
                        type="date"
                        value={formData.date_of_loss}
                        onChange={(e) => setFormData({...formData, date_of_loss: e.target.value})}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="assigned_to">Assigned To <span className="text-red-500">*</span></Label>
                      <Select 
                        value={formData.assigned_to} 
                        onValueChange={(value) => setFormData({...formData, assigned_to: value})}
                      >
                        <SelectTrigger data-testid="assigned-to-select" className="bg-white">
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                          <SelectItem value="office_web">Office/Web</SelectItem>
                          {users.map((user) => (
                            <SelectItem key={user.user_id} value={user.user_id}>
                              {user.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Additional notes..."
                      rows={4}
                    />
                  </div>

                  <div className="flex justify-end gap-4">
                    <Button type="button" variant="secondary" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-customer">
                      Create Customer
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-foreground-muted" />
            <Input
              type="search"
              placeholder="Search customers by name, email, or claim number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Customers Table */}
        <Card className="border border-border shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12" data-testid="loading-spinner">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
            </div>
          ) : customers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="customers-table">
                <thead className="bg-slate-50 text-slate-500 font-medium uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-left">Name</th>
                    <th className="px-6 py-4 text-left">Firm</th>
                    <th className="px-6 py-4 text-left">Assigned To</th>
                    <th className="px-6 py-4 text-left">Email</th>
                    <th className="px-6 py-4 text-left">Phone</th>
                    <th className="px-6 py-4 text-left">Loss Type</th>
                    <th className="px-6 py-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {customers.map((customer) => (
                    <tr 
                      key={customer.customer_id} 
                      className="hover:bg-slate-50/50 transition-colors"
                      data-testid={`customer-row-${customer.customer_id}`}
                    >
                      <td className="px-6 py-4 font-medium text-foreground">{customer.name}</td>
                      <td className="px-6 py-4 text-foreground-muted">{customer.public_adjusting_firm}</td>
                      <td className="px-6 py-4 text-foreground-muted">{customer.assigned_to_name}</td>
                      <td className="px-6 py-4 text-foreground-muted">{customer.email}</td>
                      <td className="px-6 py-4 text-foreground-muted font-mono text-xs">{customer.phone}</td>
                      <td className="px-6 py-4 text-foreground-muted">{customer.loss_type}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/customers/${customer.customer_id}`)}
                            data-testid={`view-customer-${customer.customer_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          {canAddCustomer && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(customer)}
                              data-testid={`edit-customer-${customer.customer_id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {canDeleteCustomer && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(customer.customer_id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-customer-${customer.customer_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-customers">
              <p className="text-foreground-muted">No customers found</p>
              {canAddCustomer && (
                <Button 
                  className="mt-4" 
                  variant="outline"
                  onClick={() => setIsDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Customer
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Edit Customer Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-sans">Edit Customer</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-6" data-testid="edit-customer-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_public_adjusting_firm">Public Adjusting Firm <span className="text-red-500">*</span></Label>
                  <Select 
                    value={editFormData.public_adjusting_firm} 
                    onValueChange={(value) => setEditFormData({...editFormData, public_adjusting_firm: value})}
                  >
                    <SelectTrigger data-testid="edit-firm-select" className="bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="Apicella">Apicella</SelectItem>
                      <SelectItem value="MHA">MHA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_date_of_assignment">Date of Assignment <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_date_of_assignment"
                    type="date"
                    value={editFormData.date_of_assignment}
                    onChange={(e) => setEditFormData({...editFormData, date_of_assignment: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_source">Source <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_source"
                    value={editFormData.source}
                    onChange={(e) => setEditFormData({...editFormData, source: e.target.value})}
                    placeholder="e.g., Referral, Direct"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_name">Customer Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_name"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                    placeholder="Full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_risk_address">Risk Address <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_risk_address"
                    value={editFormData.risk_address}
                    onChange={(e) => setEditFormData({...editFormData, risk_address: e.target.value})}
                    placeholder="Property address"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_phone">Phone <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_phone"
                    type="tel"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                    placeholder="(555) 555-5555"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_email">Email <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    placeholder="customer@example.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_insurance_company">Insurance Company <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_insurance_company"
                    value={editFormData.insurance_company}
                    onChange={(e) => setEditFormData({...editFormData, insurance_company: e.target.value})}
                    placeholder="Company name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_date_of_loss">Date of Loss <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_date_of_loss"
                    type="date"
                    value={editFormData.date_of_loss}
                    onChange={(e) => setEditFormData({...editFormData, date_of_loss: e.target.value})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_loss_type">Loss Type <span className="text-red-500">*</span></Label>
                  <Input
                    id="edit_loss_type"
                    value={editFormData.loss_type}
                    onChange={(e) => setEditFormData({...editFormData, loss_type: e.target.value})}
                    placeholder="e.g., Fire, Water, Wind"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_assigned_to">Assigned To <span className="text-red-500">*</span></Label>
                  <Select 
                    value={editFormData.assigned_to} 
                    onValueChange={(value) => setEditFormData({...editFormData, assigned_to: value})}
                  >
                    <SelectTrigger data-testid="edit-assigned-to-select" className="bg-white">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="office_web">Office/Web</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_notes">Notes</Label>
                <Textarea
                  id="edit_notes"
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                  placeholder="Additional notes..."
                  rows={4}
                />
              </div>

              <div className="flex justify-end gap-4">
                <Button type="button" variant="secondary" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="submit-edit-customer">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Customer Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Customer</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this customer? This action cannot be undone. Any claims associated with this customer will be preserved but will no longer be linked to this customer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteCustomerId(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={confirmDeleteCustomer}
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-delete-customer"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
